import React, {FC, useEffect, useState} from 'react';
import {useLocation} from '@reach/router';
import {PageHead} from 'components/PageHead';
import {DateSlot, TimeSlot} from 'components/TimePicker/types';
import Wrapper, {PageStyle} from 'components/Wrapper';
import {Routes} from 'constants/index';
import {navigate} from 'gatsby';
import {createBrowserHistory} from 'history';
import {useEvents} from 'hooks/useEvents';
import {createBookingLink} from 'utils/booking';
import {useQueryValues} from 'utils/queryObj';

import Booking from './Booking';
import Content from './Content';
import {useEncoded} from './useEncoded';
import {useRemaps} from './useRemaps';

// UseBooking merges both state & query string updates
const useBooking = () => {
	const [booking, set] = useState<TimeSlot>();
	const {read, update} = useQueryValues('st');
	return {
		booking,
		appt: read({appt: ''}).appt,
		setBooking: (v?: TimeSlot) => {
			set(v);
			update({appt: v?.id});
		},
	};
};

interface BookingProps {
	pageContext?: {
		name: string
		slug: string
	}
}

const BookingDetails: FC<BookingProps> = () => {
	const [pathname, setPathname] = useState<string>(useLocation().pathname);
	const [active, setActive] = useState<DateSlot>();
	const {booking, appt, setBooking} = useBooking();
	const remaps = useRemaps();

	// Attempt to parse our page args from the URL
	const {parsed, slug, date} = useEncoded(pathname);
	useEffect(() => {
		if (!parsed) {
			navigate(Routes.booking);
		} else if (slug in remaps) {
			// Do hard update to trigger full browser reload
			// instead of navigate. We want to discard all state
			// and trigger a restart of all queries.
			window.location.pathname = pathname.replace(slug, remaps[slug]);
		}
	}, [parsed, slug]);

	// Dynamically load our locations data based on the parsed slug
	const {state, refresh} = useEvents({slug});

	// Dynamically pop open booking modal if a valid date and appt is set
	useEffect(() => {
		if (booking || state.locations.length === 0) {
			return;
		} else if (!date || !appt) {
			return;
		}

		// Look through our locations to try to find a match
		for (const location of state.locations) {
			for (const curr of location.datesBase) {

				// Don't go through all dates; use our URL as a guide
				if (curr.date < date) {
					continue;
				} else if (curr.date > date) {
					break;
				}

				// Search through times to find match
				for (const time of curr.timesBase) {
					if (time.id === appt) {
						setBooking(time);
						return;
					}
				}
			}
		}

		// No match; if no queries pending, clear query arg
		if (!state.loading) {
			setBooking(undefined);
		}
	}, [state.locations.length, state.loading]);

	// If we've loaded but no location, navigate elsewhere
	useEffect(() => {
		if (state.locations.length === 0 && !state.loading) {
			navigate(Routes.booking);
		}
	}, [state.locations.length, state.loading]);

	// Render the page based on our current location
	const location = state.locations?.[0];
	return (
		<Wrapper style={PageStyle.ContentPage}>

			{/* Render our page content showing available slots */}
			<Content
				location={location}
				slug={slug}
				initial={date}
				loading={state.loading}
				active={active}
				callbacks={{
					onDate: (d: DateSlot) => {
						// Track our active date from selector
						setActive(d);

						// Update URL if selected date changes
						const pathname = createBookingLink({
							slug,
							date: d.date,
						});

						// Only issue replace if we have a path change
						const history = createBrowserHistory();
						if (history.location.pathname !== pathname) {
							history.replace({
								pathname,
								search: history.location.search,
							});
							setPathname(pathname);
						}
					},
					onTime: (t: TimeSlot) => {
						setBooking(t);
						return false;
					},
				}}
			/>

			{/* Exclusively show our booking or success modal */}
			{booking && (
				<Booking
					time={booking}
					onHide={() => {
						setBooking(undefined);
						refresh();
					}}
				/>
			)}
		</Wrapper>
	);
};

export default BookingDetails;

const trimDate = (pathname: string, slug?: string) => {
	// Guard clauses – if either string is missing or the slug isn’t in the path, leave it unchanged
	if (!pathname || !slug) return pathname;

	const pos = pathname.indexOf(slug);
	if (pos === -1) return pathname;

	// Keep everything from the start of the pathname through the end of the slug
	return pathname.slice(0, pos + slug.length);
};

export const Head = PageHead<BookingProps>(
	({location, pageContext}) => ({
		title: pageContext?.name ?
			`Book your DEXA scan at ${pageContext.name}` :
			'Book your DEXA scan',

		canonical: trimDate(location.pathname, pageContext?.slug),
	})
);
